/**
 * Softphone Store
 * Combines call state and call controls into a single store
 *
 * NOTE: WebRTC state is managed internally by WebRTCService (singleton),
 * not in Zustand, to avoid state synchronization complexity.
 */
import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { createCallControlsSlice } from './slices/callControls';
import { CallControlsSlice } from './slices/callControls/index.types';
import { createCallSlice, CallSlice } from './slices/callSlice';
import { createCollapsibleSlice } from './slices/collapsibleSlice';
import { CollapsibleSlice } from './slices/collapsibleSlice/index.types';
import { createConferenceSlice, ConferenceSlice } from './slices/conferenceSlice';
import { createDevicesSlice, DevicesSlice } from './slices/devicesSlice';
import { createDialpadSlice } from './slices/dialpadSlice';
import { DialpadSlice } from './slices/dialpadSlice/index.types';
import { createKeypadSlice } from './slices/keypadSlice';
import { KeypadSlice } from './slices/keypadSlice/index.types';
import { createSipSlice, SipSlice } from './slices/sipSlice';
import { createTransferSlice, TransferSlice } from './slices/transferSlice';
import { createWarmTransferSlice, WarmTransferSlice } from './slices/warmTransferSlice';

export type SoftphoneStore = SipSlice &
  DevicesSlice &
  CallSlice &
  CallControlsSlice &
  ConferenceSlice &
  KeypadSlice &
  DialpadSlice &
  CollapsibleSlice &
  TransferSlice &
  WarmTransferSlice;

export const useSoftphoneStore = create<SoftphoneStore>()(
  devtools(
    subscribeWithSelector((...args) => ({
      ...createSipSlice(...args),
      ...createDevicesSlice(...args),
      ...createCallSlice(...args),
      ...createCallControlsSlice(...args),
      ...createConferenceSlice(...args),
      ...createKeypadSlice(...args),
      ...createDialpadSlice(...args),
      ...createCollapsibleSlice(...args),
      ...createTransferSlice(...args),
      ...createWarmTransferSlice(...args),
    })),
    { name: 'SoftphoneStore' }
  )
);

export type { SipSlice, SipState, SipActions } from './slices/sipSlice';
export type {
  DevicesSlice,
  DevicesState,
  DevicesActions,
} from './slices/devicesSlice';
export type { CallSlice, CallState, CallActions } from './slices/callSlice';
export type {
  ConferenceSlice,
  ConferenceState,
  ConferenceActions,
} from './slices/conferenceSlice';
export type { DialpadSlice, DialpadSliceState } from './slices/dialpadSlice/index.types';
export type { KeypadSlice, KeypadSliceState } from './slices/keypadSlice/index.types';
export type {
  TransferSlice,
  TransferSliceState,
  TransferSliceActions,
} from './slices/transferSlice';
export type {
  WarmTransferSlice,
  WarmTransferSliceState,
  WarmTransferSliceActions,
  WarmTransferContext,
  WarmTransferState,
} from './slices/warmTransferSlice';

// Selectors
export { selectPermissions, selectWarmTransferConsultation } from './selectors';
export type { WarmTransferConsultation } from './selectors';

/**
 * Re-exports for convenience:
 * - Call Controls Store: Manages call control states (mute, hold, etc.), global and non-persisted
 * - Coordinator Store: Manages softphone preference (legacy vs new), persisted by personId
 */

export {
  createSoftphoneCoordinatorStore,
  useSoftphoneCoordinatorStore,
} from './coordinator';

export {
  type SoftphoneCoordinatorStore,
  type SoftphoneCoordinatorState,
  type SoftphoneCoordinatorActions,
} from './coordinator/index.types';
